import $api from '../http/axios';

import { IS3File } from './interfaces';
import axios from 'axios';
import { env } from 'env';

export interface IUploadFileName {
  name: string;
  contentType?: string;
}

export interface IUploadFileUrl {
  name: string;
  location: string;
  url: string;
  uploadUrl: string;
  contentType?: string;
}

export type VideoTranscodeStatusValue =
  | 'PENDING'
  | 'PROCESSING'
  | 'SUCCEEDED'
  | 'FAILED'
  | 'CANCELLED';

export interface ICreateVideoTranscodeJobResponse {
  jobId: string;
  status: VideoTranscodeStatusValue;
  uploadUrl: string;
  outputUrl: string;
}

export interface IVideoTranscodeJobStatus {
  jobId: string;
  status: VideoTranscodeStatusValue;
  outputUrl?: string;
  error?: { code: string; message: string };
}

export type VideoTranscodeClientErrorCode = 'cancelled' | 'failed' | 'timeout';

export class VideoTranscodeClientError extends Error {
  code: VideoTranscodeClientErrorCode;

  constructor(code: VideoTranscodeClientErrorCode, message?: string) {
    super(message || code);
    this.name = 'VideoTranscodeClientError';
    this.code = code;
    Object.setPrototypeOf(this, VideoTranscodeClientError.prototype);
  }
}

export interface ITranscodeVideoOptions {
  signal?: AbortSignal;
  onUploadProgress?: (percent: number) => void;
  onPhaseChange?: (phase: 'uploading' | 'transcoding') => void;
}

const TRANSCODE_POLL_INTERVAL_MS = 2500;
const TRANSCODE_MAX_WAIT_MS = 16 * 60 * 1000;

const getKeyFromUrl = (url: string): string => {
  try {
    return new URL(url).pathname.replace(/^\/+/, '');
  } catch {
    return url;
  }
};

export default class FilesService {
  static getS3Files(files: string[]): Promise<IS3File[]> {
    return $api
      .post(
        'cms/files/retrieveFiles',
        { files },
        {
          validateStatus: (status) => {
            return status !== 403;
          },
        },
      )
      .then((res) => (res.data ? res.data.files : []));
  }

  static getUploadUrls(files: IUploadFileName[]): Promise<IUploadFileUrl[]> {
    return axios
      .post(`${env.REACT_APP_API_URL}api/v2/files/upload-urls`, {
        files,
      })
      .then((res) => res.data as IUploadFileUrl[]);
  }

  static async uploadFiles(files: File[]): Promise<IUploadFileUrl[]> {
    return this.uploadFilesWithProgress(files);
  }

  static async uploadFilesWithProgress(
    files: File[],
    onUploadProgress?: (percent: number) => void,
  ): Promise<IUploadFileUrl[]> {
    const urlsToPut = await this.getUploadUrls(
      files.map((file) => ({ name: file.name, contentType: file.type })),
    );

    const config = {
      headers: {
        'Content-Type': 'application/json',
      },
    };

    const axiosInstance = axios.create(config);

    axiosInstance.interceptors.request.use((config) => {
      delete (config.headers as any).Authorization;
      return config;
    });

    let totalUploaded = 0;
    const fileSizes = files.map((f) => f.size || 1);
    const totalSize = fileSizes.reduce((a, b) => a + b, 0) || 1;

    await Promise.all(
      urlsToPut.map(({ uploadUrl, contentType }, index) => {
        let prevLoaded = 0;
        return axiosInstance.put(uploadUrl, files[index], {
          headers: contentType
            ? { 'Content-Type': contentType, Authorization: `` }
            : {},
          sendAuth0Headers: false,
          onUploadProgress: onUploadProgress
            ? (event: any) => {
                const loaded = event.loaded ?? 0;
                totalUploaded += loaded - prevLoaded;
                prevLoaded = loaded;
                const percent = Math.round(
                  Math.min((totalUploaded / totalSize) * 100, 100),
                );
                onUploadProgress(percent);
              }
            : undefined,
        });
      }),
    );

    return urlsToPut;
  }

  static requestVideoTranscode(
    name: string,
    contentType?: string,
  ): Promise<ICreateVideoTranscodeJobResponse> {
    return $api
      .post('api/v2/files/transcode', { name, contentType })
      .then((res) => res.data as ICreateVideoTranscodeJobResponse);
  }

  static getVideoTranscodeStatus(
    jobId: string,
  ): Promise<IVideoTranscodeJobStatus> {
    return $api
      .get(`api/v2/files/transcode/${jobId}`)
      .then((res) => res.data as IVideoTranscodeJobStatus);
  }

  static cancelVideoTranscode(
    jobId: string,
  ): Promise<IVideoTranscodeJobStatus> {
    return $api
      .post(`api/v2/files/transcode/${jobId}/cancel`)
      .then((res) => res.data as IVideoTranscodeJobStatus);
  }

  static async transcodeVideoViaServer(
    file: File,
    options: ITranscodeVideoOptions = {},
  ): Promise<{ url: string; location: string }> {
    const { signal, onUploadProgress, onPhaseChange } = options;

    const { jobId, uploadUrl, outputUrl } = await this.requestVideoTranscode(
      file.name,
      file.type,
    );

    onPhaseChange?.('uploading');

    const uploadClient = axios.create();

    uploadClient.interceptors.request.use((config) => {
      delete (config.headers as any).Authorization;
      return config;
    });

    try {
      await uploadClient.put(uploadUrl, file, {
        headers: file.type ? { 'Content-Type': file.type } : {},
        signal,
        onUploadProgress: onUploadProgress
          ? (event: any) => {
              const total = event.total || file.size || 1;
              onUploadProgress(
                Math.round(Math.min((event.loaded ?? 0) / total, 1) * 100),
              );
            }
          : undefined,
      });
    } catch (uploadError) {
      if (signal?.aborted || axios.isCancel?.(uploadError)) {
        await this.cancelVideoTranscode(jobId).catch(() => undefined);
        throw new VideoTranscodeClientError('cancelled');
      }
      throw uploadError;
    }

    onPhaseChange?.('transcoding');

    const deadline = Date.now() + TRANSCODE_MAX_WAIT_MS;

    while (Date.now() < deadline) {
      if (signal?.aborted) {
        await this.cancelVideoTranscode(jobId).catch(() => undefined);
        throw new VideoTranscodeClientError('cancelled');
      }

      await new Promise<void>((resolve) => {
        setTimeout(resolve, TRANSCODE_POLL_INTERVAL_MS);
      });

      let status: IVideoTranscodeJobStatus;

      try {
        status = await this.getVideoTranscodeStatus(jobId);
      } catch {
        continue;
      }

      if (status.status === 'SUCCEEDED') {
        const url = status.outputUrl || outputUrl;
        return { url, location: getKeyFromUrl(url) };
      }

      if (status.status === 'FAILED') {
        throw new VideoTranscodeClientError('failed', status.error?.message);
      }

      if (status.status === 'CANCELLED') {
        throw new VideoTranscodeClientError('cancelled');
      }
    }

    await this.cancelVideoTranscode(jobId).catch(() => undefined);
    throw new VideoTranscodeClientError('timeout');
  }

  static saveFormData(data: {
    name: string;
    formData: string;
    eventId?: string;
  }) {
    return axios
      .post(`${env.REACT_APP_API_URL}api/v1/profile/participant/feedback`, {
        ...data,
      })
      .then((res) => res.data);
  }

  static async downloadFileFromS3(url: string) {
    const axiosInstance = axios.create({});

    axiosInstance.interceptors.request.use((config) => {
      delete (config.headers as any).Authorization;

      return config;
    });

    const response = await axiosInstance.get(url, {
      responseType: 'arraybuffer',
    });

    return response.data;
  }
}
